// src/pages/LibraryPage.jsx
import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../firebase";
import Layout from "../components/Layout";
import { useStudent } from "../hooks/useStudent";
import AudioPlayerOverlay from "../components/AudioPlayerOverlay";
import { useTranslation } from "react-i18next";

// ── Thumbnail colour palette derived from title ───────────────────────────────
const PALETTES = [
  { bg:"#EDE9FE", grad:"135deg,#C4B5FD,#7C3AED", icon:"#5B21B6" },
  { bg:"#FCE7F3", grad:"135deg,#FBCFE8,#DB2777", icon:"#9D174D" },
  { bg:"#D1FAE5", grad:"135deg,#A7F3D0,#059669", icon:"#065F46" },
  { bg:"#DBEAFE", grad:"135deg,#BFDBFE,#2563EB", icon:"#1E40AF" },
  { bg:"#FEF3C7", grad:"135deg,#FDE68A,#D97706", icon:"#92400E" },
  { bg:"#FFE4E6", grad:"135deg,#FECDD3,#E11D48", icon:"#9F1239" },
  { bg:"#E0F2FE", grad:"135deg,#BAE6FD,#0284C7", icon:"#0C4A6E" },
  { bg:"#F0FDF4", grad:"135deg,#BBF7D0,#16A34A", icon:"#14532D" },
];

function titlePalette(title) {
  let h = 0;
  for (const c of (title || "")) h = c.charCodeAt(0) + ((h << 5) - h);
  return PALETTES[Math.abs(h) % PALETTES.length];
}

function initials(title) {
  const words = (title || "?").trim().split(/\s+/);
  return words.length === 1
    ? words[0].slice(0, 2).toUpperCase()
    : (words[0][0] + words[1][0]).toUpperCase();
}

// ── Type meta ─────────────────────────────────────────────────────────────────
const TYPE_META = {
  audio: { label:"AUDIO", bg:"#dbeafe", color:"var(--badge-audio-text, #93C5FD)", icon:"🎵" },
  pdf:   { label:"PDF",   bg:"#fee2e2", color:"var(--error-text)", icon:"📄" },
  image: { label:"IMAGE", bg:"#dcfce7", color:"var(--green-text)", icon:"🖼️" },
  video: { label:"VIDEO", bg:"#fef3c7", color:"var(--amber-text)", icon:"🎬" },
};
function typeStyle(t) {
  return TYPE_META[t] || { label:"FILE", bg:"#f3f4f6", color:"var(--text-label)", icon:"📎" };
}

// ── Smart icon detection (folder & file thumbnails) ───────────────────────────
// Rules are checked in order — first match wins.
const ICON_RULES = [
  // ── Instruments ──────────────────────────────────────────────────────────
  { re: /harmonium/i,                                  icon: "🪗", label: "Harmonium"  },
  { re: /piano|keyboard/i,                             icon: "🎹", label: "Piano"      },
  { re: /tabla/i,                                      icon: "🥁", label: "Tabla"      },
  { re: /mridangam|pakhawaj|dholak|dhol|percussion/i,  icon: "🥁", label: "Percussion" },
  { re: /sitar|sarod|veena|vina/i,                     icon: "🎸", label: "Strings"    },
  { re: /violin|sarangi|fiddle/i,                      icon: "🎻", label: "Violin"     },
  { re: /flute|bansuri/i,                              icon: "🎵", label: "Flute"      },
  { re: /guitar/i,                                     icon: "🎸", label: "Guitar"     },
  { re: /santoor|harp/i,                               icon: "🎹", label: "Santoor"    },
  { re: /shehnai|trumpet|brass|wind/i,                 icon: "🎺", label: "Wind"       },
  // ── Vocal / singing ───────────────────────────────────────────────────────
  { re: /vocal|singing|voice|gaan|gaayki|singer/i,     icon: "🎤", label: "Vocal"      },
  { re: /aakar|swar\b|swara\b|sur\b/i,                 icon: "🎤", label: "Vocal"      },
  // ── Written material / notation ───────────────────────────────────────────
  { re: /notation|swarlipi|lipi|sheet music|score/i,   icon: "🎼", label: "Notation"   },
  { re: /theory|textbook|syllabus|notes\b/i,           icon: "📚", label: "Theory"     },
  { re: /book|reading|study|material/i,                icon: "📚", label: "Study"      },
  // ── Devotional / classical genres ─────────────────────────────────────────
  { re: /bhajan|devotional|aarti|kirtan|stotra/i,      icon: "🙏", label: "Devotional" },
  { re: /raag|raga|ragam/i,                            icon: "🎵", label: "Raga"       },
  { re: /taal|tala|tal\b|laya|rhythm/i,                icon: "🥁", label: "Taal"       },
  { re: /bandish|kriti|khayal|composition|bandh/i,     icon: "📝", label: "Composition"},
  { re: /alankar|alankaar|exercise|warm.?up/i,         icon: "🔁", label: "Practice"   },
  { re: /riyaz|practice|abhyas/i,                      icon: "🔁", label: "Riyaz"      },
  // ── Format / media type hints ─────────────────────────────────────────────
  { re: /video|watch|lesson.?video|tutorial/i,         icon: "🎬", label: "Video"      },
  { re: /audio|listen|recording|mp3|wav/i,             icon: "🎧", label: "Audio"      },
  { re: /image|photo|picture|diagram/i,                icon: "🖼️", label: "Image"      },
  // ── Curriculum / learning ─────────────────────────────────────────────────
  { re: /lesson|class|course|chapter|level/i,          icon: "🎓", label: "Lesson"     },
  { re: /folk|lokgeet|lok geet|lokgeete/i,             icon: "🪘", label: "Folk"       },
  { re: /film|bollywood|movie|cinema|filmi/i,          icon: "🎬", label: "Film"       },
  { re: /carnatic|classical|hindustani|shastriya/i,    icon: "🎵", label: "Classical"  },
  { re: /light music|sugam|semi.?classical/i,          icon: "🎶", label: "Light Music"},
];

/**
 * Returns the best emoji for a folder.
 * Priority: 1) manually saved icon field  2) keyword auto-detection  3) 📂 default
 */
function smartFolderIcon(name, description, savedIcon) {
  if (savedIcon) return savedIcon;
  const text = `${name || ""} ${description || ""}`;
  for (const rule of ICON_RULES) {
    if (rule.re.test(text)) return rule.icon;
  }
  return "📂";
}

/**
 * Returns the best emoji for a file thumbnail.
 * Priority: 1) manually saved icon field  2) keyword auto-detection  3) type icon
 */
function smartTrackIcon(track) {
  if (track.icon) return track.icon;
  const text = `${track.title || ""} ${track.description || ""}`;
  for (const rule of ICON_RULES) {
    if (rule.re.test(text)) return rule.icon;
  }
  return typeStyle(track.type).icon;
}

// ── View toggle ───────────────────────────────────────────────────────────────
function ListIcon({ active }) {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <rect x="1" y="3"    width="16" height="2.5" rx="1" fill={active ? "var(--purple-text)" : "#9CA3AF"}/>
      <rect x="1" y="7.75" width="16" height="2.5" rx="1" fill={active ? "var(--purple-text)" : "#9CA3AF"}/>
      <rect x="1" y="12.5" width="16" height="2.5" rx="1" fill={active ? "var(--purple-text)" : "#9CA3AF"}/>
    </svg>
  );
}
function GridIcon({ active }) {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <rect x="1"  y="1"  width="7" height="7" rx="1.5" fill={active ? "var(--purple-text)" : "#9CA3AF"}/>
      <rect x="10" y="1"  width="7" height="7" rx="1.5" fill={active ? "var(--purple-text)" : "#9CA3AF"}/>
      <rect x="1"  y="10" width="7" height="7" rx="1.5" fill={active ? "var(--purple-text)" : "#9CA3AF"}/>
      <rect x="10" y="10" width="7" height="7" rx="1.5" fill={active ? "var(--purple-text)" : "#9CA3AF"}/>
    </svg>
  );
}
function ViewToggle({ view, setView }) {
  return (
    <div style={{ display:"flex", gap:"2px", background:"#F3F4F6",
                  borderRadius:"8px", padding:"3px" }}>
      {[["list", <ListIcon active={view==="list"}/>],
        ["grid", <GridIcon active={view==="grid"}/>]].map(([v, ico]) => (
        <button key={v} onClick={() => setView(v)} style={{
          display:"flex", alignItems:"center", justifyContent:"center",
          width:"32px", height:"28px", border:"none", borderRadius:"6px",
          cursor:"pointer", transition:"background .15s",
          background: view === v ? "white" : "transparent",
          boxShadow: view === v ? "0 1px 4px rgba(0,0,0,.1)" : "none",
        }}>
          {ico}
        </button>
      ))}
    </div>
  );
}

// ── Breadcrumb ────────────────────────────────────────────────────────────────
function Breadcrumb({ parts }) {
  return (
    <div style={{ display:"flex", alignItems:"center", gap:"8px",
                  marginBottom:"16px", fontSize:"1.05rem", color:"var(--text-muted)",
                  flexWrap:"wrap" }}>
      {parts.map((p, i) => (
        <React.Fragment key={i}>
          {i > 0 && (
            <span style={{ color:"var(--text-muted)", fontSize:"1.2rem", lineHeight:1 }}>›</span>
          )}
          {p.onClick ? (
            <span onClick={p.onClick}
              style={{ color:"var(--purple-text)", cursor:"pointer", fontWeight:"700",
                       textDecoration:"underline", textUnderlineOffset:"3px" }}>
              {p.label}
            </span>
          ) : (
            <span style={{ color:"var(--text)", fontWeight:"800" }}>{p.label}</span>
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

// ── Shared thumbnail grid (2–3 per row) ───────────────────────────────────────
const THUMB_GRID = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))",
  gap: "14px",
};

// ── Folder card (thumbnail) ───────────────────────────────────────────────────
function FolderCard({ folder, onClick }) {
  const [hover, setHover] = useState(false);
  const pal = titlePalette(folder.name);
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        background:"white", borderRadius:"14px", overflow:"hidden",
        border: hover ? "2px solid #7B1515" : "2px solid #E5E7EB",
        cursor:"pointer",
        boxShadow: hover ? "0 4px 16px rgba(123,21,21,.13)" : "0 1px 4px rgba(0,0,0,.06)",
        transition:"border-color .2s, box-shadow .2s",
        display:"flex", flexDirection:"column",
      }}
    >
      <div style={{
        height:"110px", flexShrink:0,
        background:`linear-gradient(${pal.grad})`,
        display:"flex", flexDirection:"column",
        alignItems:"center", justifyContent:"center", gap:"6px",
      }}>
        <div style={{ fontSize:"2.4rem", lineHeight:1 }}>
          {smartFolderIcon(folder.name, folder.description, folder.icon)}
        </div>
        <div style={{
          background:"rgba(255,255,255,.28)", borderRadius:"99px",
          padding:"2px 10px", fontSize:".72rem", fontWeight:"800",
          color:"white", letterSpacing:".5px",
          textShadow:"0 1px 3px rgba(0,0,0,.3)",
          maxWidth:"80%", overflow:"hidden",
          whiteSpace:"nowrap", textOverflow:"ellipsis", textAlign:"center",
        }}>
          {initials(folder.name)}
        </div>
      </div>
      <div style={{ padding:"10px 12px", flex:1 }}>
        <div style={{
          fontWeight:"700", fontSize:".88rem", color:"var(--text)",
          overflow:"hidden", display:"-webkit-box",
          WebkitLineClamp:2, WebkitBoxOrient:"vertical", lineHeight:1.3,
        }}>
          {folder.name}
        </div>
        {folder.description && (
          <div style={{
            fontSize:".72rem", color:"var(--text-muted)", marginTop:"3px",
            overflow:"hidden", display:"-webkit-box",
            WebkitLineClamp:1, WebkitBoxOrient:"vertical",
          }}>
            {folder.description}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Folder list row ───────────────────────────────────────────────────────────
function FolderRow({ folder, onClick, last }) {
  const pal = titlePalette(folder.name);
  return (
    <div
      onClick={onClick}
      style={{
        display:"flex", alignItems:"center", gap:"14px",
        padding:"14px 16px", cursor:"pointer",
        borderBottom: last ? "none" : "1px solid #F3F4F6",
        transition:"background .12s",
      }}
      onMouseEnter={e => e.currentTarget.style.background = "#F9F5FF"}
      onMouseLeave={e => e.currentTarget.style.background = "transparent"}
    >
      <div style={{
        width:"46px", height:"46px", flexShrink:0,
        background:`linear-gradient(${pal.grad})`,
        borderRadius:"12px",
        display:"flex", alignItems:"center", justifyContent:"center",
        fontSize:"1.6rem",
      }}>
        {smartFolderIcon(folder.name, folder.description, folder.icon)}
      </div>
      <div style={{ flex:1, minWidth:0 }}>
        <div style={{ fontWeight:"700", fontSize:"1rem", color:"var(--text)" }}>
          {folder.name}
        </div>
        {folder.description && (
          <div style={{ fontSize:".8rem", color:"var(--text-muted)", marginTop:"2px",
                        overflow:"hidden", whiteSpace:"nowrap", textOverflow:"ellipsis" }}>
            {folder.description}
          </div>
        )}
      </div>
      <span style={{ color:"var(--purple-text)", fontSize:"1.4rem", fontWeight:"700",
                     flexShrink:0, lineHeight:1 }}>›</span>
    </div>
  );
}

// ── File list row ─────────────────────────────────────────────────────────────
function TrackRow({ track, onPlay }) {
  const { t } = useTranslation();
  const trackUrl  = track.url || track.fileUrl || track.audioUrl || "";
  const trackType = (track.type || "").toLowerCase();
  const isAudio   = trackType === "audio" ||
                    (!trackType && trackUrl.includes("firebasestorage.googleapis.com")) ||
                    (!trackType && /\.(mp3|m4a|wav|ogg|aac)(\?|$)/i.test(trackUrl));
  const s = typeStyle(isAudio ? "audio" : trackType);
  return (
    <div className="track-row">
      <span style={{ fontSize:"1.2rem", flexShrink:0 }}>{smartTrackIcon(track)}</span>
      <div style={{ flex:1, minWidth:0 }}>
        <div style={{ fontWeight:"600", fontSize:".9rem" }}>{track.title}</div>
        {track.description && <div className="muted">{track.description}</div>}
      </div>
      <span style={{ display:"inline-block", padding:"2px 8px", borderRadius:"99px",
                     fontSize:".7rem", fontWeight:"700",
                     background:s.bg, color:s.color, flexShrink:0 }}>
        {s.label}
      </span>
      {isAudio ? (
        <button className="btn btn-primary btn-sm" style={{ flexShrink:0 }}
          onClick={() => onPlay && onPlay(track)}>
          {t("common.play")}
        </button>
      ) : (
        <a href={trackUrl} target="_blank" rel="noreferrer"
          className="btn btn-primary btn-sm" style={{ flexShrink:0 }}>
          {trackType === "video" ? t("common.watch") : t("common.openFile")}
        </a>
      )}
    </div>
  );
}

// ── File thumbnail card ───────────────────────────────────────────────────────
function TrackCard({ track, onPlay }) {
  const { t } = useTranslation();
  const trackUrl  = track.url || track.fileUrl || track.audioUrl || "";
  const trackType = (track.type || "").toLowerCase();
  const isAudio   = trackType === "audio" ||
                    (!trackType && trackUrl.includes("firebasestorage.googleapis.com")) ||
                    (!trackType && /\.(mp3|m4a|wav|ogg|aac)(\?|$)/i.test(trackUrl));
  const s   = typeStyle(isAudio ? "audio" : trackType);
  const pal = titlePalette(track.title);
  const [hover, setHover] = useState(false);

  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        background:"white", borderRadius:"14px", overflow:"hidden",
        border: hover ? "2px solid #7B1515" : "2px solid #E5E7EB",
        boxShadow: hover ? "0 6px 20px rgba(123,21,21,.14)" : "0 1px 4px rgba(0,0,0,.07)",
        transition:"border-color .18s, box-shadow .18s",
        display:"flex", flexDirection:"column",
        cursor:"pointer",
      }}
    >
      <div style={{
        background:`linear-gradient(${pal.grad})`,
        height:"110px", flexShrink:0,
        display:"flex", flexDirection:"column",
        alignItems:"center", justifyContent:"center",
        gap:"6px", position:"relative",
      }}>
        <span style={{
          position:"absolute", top:"8px", right:"8px",
          background:"rgba(255,255,255,.85)", borderRadius:"6px",
          padding:"2px 7px", fontSize:".65rem", fontWeight:"700",
          color:s.color,
        }}>
          {s.label}
        </span>
        <div style={{ fontSize:"2.4rem", lineHeight:1 }}>
          {smartTrackIcon(track)}
        </div>
        <div style={{
          background:"rgba(255,255,255,.25)", borderRadius:"99px",
          padding:"3px 10px", fontSize:".78rem", fontWeight:"800",
          color:"white", letterSpacing:".5px", textShadow:"0 1px 3px rgba(0,0,0,.3)",
        }}>
          {initials(track.title)}
        </div>
      </div>
      <div style={{ padding:"12px", flex:1, display:"flex", flexDirection:"column", gap:"8px" }}>
        <div>
          <div style={{ fontWeight:"700", fontSize:".88rem", color:"var(--text)",
                        overflow:"hidden", display:"-webkit-box",
                        WebkitLineClamp:2, WebkitBoxOrient:"vertical",
                        lineHeight:1.35 }}>
            {track.title}
          </div>
          {track.description && (
            <div style={{ fontSize:".72rem", color:"var(--text-muted)", marginTop:"3px",
                          overflow:"hidden", display:"-webkit-box",
                          WebkitLineClamp:1, WebkitBoxOrient:"vertical" }}>
              {track.description}
            </div>
          )}
        </div>
        {isAudio ? (
          <button className="btn btn-primary btn-sm"
            style={{ width:"100%", marginTop:"auto" }}
            onClick={() => onPlay && onPlay(track)}>
            {t("common.play")}
          </button>
        ) : (
          <a href={trackUrl} target="_blank" rel="noreferrer"
            className="btn btn-primary btn-sm"
            style={{ width:"100%", marginTop:"auto", textAlign:"center" }}>
            {trackType === "video" ? t("common.watch") : t("common.openFile")}
          </a>
        )}
      </div>
    </div>
  );
}

// ── File list (switches between list rows and thumbnail grid) ─────────────────
function TrackList({ tracks, view, onPlay }) {
  if (view === "grid") {
    return (
      <div style={{ ...THUMB_GRID, paddingTop:"4px" }}>
        {tracks.map(t => <TrackCard key={t.id} track={t} onPlay={onPlay}/>)}
      </div>
    );
  }
  return <>{tracks.map(t => <TrackRow key={t.id} track={t} onPlay={onPlay}/>)}</>;
}

// ── Section header with optional toggle ───────────────────────────────────────
function SectionHeader({ title, count, view, setView, badge }) {
  return (
    <div style={{ display:"flex", alignItems:"center",
                  justifyContent:"space-between", marginBottom:"12px" }}>
      <span style={{ fontWeight:"700", fontSize:"1rem", color:"var(--text)" }}>
        {title}
      </span>
      <div style={{ display:"flex", alignItems:"center", gap:"10px" }}>
        {count !== undefined && (
          <span className="badge badge-purple">
            {count} {badge || (count !== 1 ? "items" : "item")}
          </span>
        )}
        {view && <ViewToggle view={view} setView={setView}/>}
      </div>
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────
export default function LibraryPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { student } = useStudent();
  const { t } = useTranslation();

  const [activeTab,     setActiveTab]     = useState("content");
  const [folders,       setFolders]       = useState([]);
  const [subfolders,    setSubfolders]    = useState([]);
  const [directTracks,  setDirectTracks]  = useState([]);
  const [subTracks,     setSubTracks]     = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [folderLoading, setFolderLoading] = useState(false);
  const [subLoading,    setSubLoading]    = useState(false);
  const [playingTrack,  setPlayingTrack]  = useState(null);

  const [view, setViewRaw] = useState(
    () => localStorage.getItem("libraryView") || "list"
  );
  function setView(v) { setViewRaw(v); localStorage.setItem("libraryView", v); }

  // Navigation state comes entirely from the URL
  const folderId    = searchParams.get("folder");
  const subfolderId = searchParams.get("sub");

  // Derive active objects from loaded arrays + URL
  const activeFolder    = folderId    ? folders.find(f => f.id === folderId)    ?? null : null;
  const activeSubfolder = subfolderId ? subfolders.find(s => s.id === subfolderId) ?? null : null;

  // Nav helpers — these update the URL, which React Router puts in history
  // so the browser back button naturally walks back through the hierarchy
  const goToLibrary = () => setSearchParams({});
  const goToFolder  = () => setSearchParams({ folder: folderId });
  const openFolder  = (f) => setSearchParams({ folder: f.id });
  const openSubfolder = (s) => setSearchParams({ folder: folderId, sub: s.id });

  // Load top-level folders once
  useEffect(() => {
    getDocs(collection(db, "library")).then(snap => {
      setFolders(
        snap.docs.map(d => ({ id: d.id, ...d.data() }))
          .sort((a, b) => (a.order || 0) - (b.order || 0))
      );
      setLoading(false);
    });
  }, []);

  // Load folder contents whenever folderId changes
  useEffect(() => {
    if (!folderId) {
      setSubfolders([]);
      setDirectTracks([]);
      return;
    }
    setFolderLoading(true);
    setSubfolders([]);
    setDirectTracks([]);
    Promise.all([
      getDocs(collection(db, "library", folderId, "subfolders")),
      getDocs(collection(db, "library", folderId, "tracks")),
    ]).then(([subSnap, trackSnap]) => {
      setSubfolders(
        subSnap.docs.map(d => ({ id: d.id, ...d.data() }))
          .sort((a, b) => (a.order || 0) - (b.order || 0))
      );
      setDirectTracks(trackSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setFolderLoading(false);
    });
  }, [folderId]);

  // Load subfolder tracks whenever subfolderId changes
  useEffect(() => {
    if (!subfolderId || !folderId) {
      setSubTracks([]);
      return;
    }
    setSubLoading(true);
    setSubTracks([]);
    getDocs(
      collection(db, "library", folderId, "subfolders", subfolderId, "tracks")
    ).then(snap => {
      setSubTracks(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setSubLoading(false);
    });
  }, [subfolderId, folderId]);

  // ── Subfolder contents view ────────────────────────────────────────────────
  if (subfolderId) {
    const subName = activeSubfolder?.name || subfolderId;
    const folderName = activeFolder?.name || folderId;
    return (
      <Layout title={`📁 ${subName}`} student={student}>
        <Breadcrumb parts={[
          { label: t("library.title"), onClick: goToLibrary },
          { label: folderName, onClick: goToFolder },
          { label: subName },
        ]}/>
        {subLoading ? (
          <div className="empty-state"><div className="icon">📁</div><p>{t("common.loading")}</p></div>
        ) : subTracks.length === 0 ? (
          <div className="empty-state"><div className="icon">📭</div><p>{t("library.noFiles")}</p></div>
        ) : (
          <div className="card">
            <SectionHeader
              title={`📁 ${subName}`}
              count={subTracks.length}
              view={view} setView={setView}
            />
            <TrackList tracks={subTracks} view={view} onPlay={setPlayingTrack}/>
          </div>
        )}
        <AudioPlayerOverlay track={playingTrack} onClose={() => setPlayingTrack(null)}/>
      </Layout>
    );
  }

  // ── Folder contents view ───────────────────────────────────────────────────
  if (folderId) {
    const folderName = activeFolder?.name || folderId;
    return (
      <Layout title={`📂 ${folderName}`} student={student}>
        <Breadcrumb parts={[
          { label: t("library.title"), onClick: goToLibrary },
          { label: folderName },
        ]}/>

        {folderLoading ? (
          <div className="empty-state"><div className="icon">📂</div><p>{t("common.loading")}</p></div>
        ) : (
          <>
            {/* Subfolders */}
            {subfolders.length > 0 && (
              <div style={{ marginBottom:"20px" }}>
                <SectionHeader
                  title={t("library.subfolders")}
                  view={view} setView={setView}
                />
                {view === "grid" ? (
                  <div style={THUMB_GRID}>
                    {subfolders.map(sub => (
                      <FolderCard key={sub.id} folder={sub}
                        onClick={() => openSubfolder(sub)}/>
                    ))}
                  </div>
                ) : (
                  <div className="card" style={{ padding:0, overflow:"hidden" }}>
                    {subfolders.map((sub, i) => (
                      <FolderRow key={sub.id} folder={sub}
                        onClick={() => openSubfolder(sub)}
                        last={i === subfolders.length - 1}/>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Direct files */}
            {directTracks.length > 0 && (
              <div className="card">
                <SectionHeader
                  title={t("library.files")}
                  count={directTracks.length}
                  view={view} setView={setView}
                />
                <TrackList tracks={directTracks} view={view} onPlay={setPlayingTrack}/>
              </div>
            )}

            {subfolders.length === 0 && directTracks.length === 0 && (
              <div className="empty-state"><div className="icon">📭</div><p>{t("library.noContent")}</p></div>
            )}
          </>
        )}
        <AudioPlayerOverlay track={playingTrack} onClose={() => setPlayingTrack(null)}/>
      </Layout>
    );
  }

  // ── Top-level library view ─────────────────────────────────────────────────
  return (
    <Layout title={`📚 ${t("library.title")}`} student={student}>

      {loading ? (
        <div className="empty-state"><div className="icon">📚</div><p>{t("common.loading")}</p></div>
      ) : folders.length === 0 ? (
        <div className="empty-state">
          <div className="icon">📚</div><p>{t("library.noContent")}</p>
        </div>
      ) : (
        <div>
          <div style={{ display:"flex", alignItems:"flex-start",
                        justifyContent:"space-between", marginBottom:"20px", gap:"12px" }}>
            <div>
              <h2 style={{ fontSize:"1.1rem", fontWeight:"800", color:"var(--purple-text)" }}>
                📚 {t("library.title")}
              </h2>
              <p style={{ fontSize:".85rem", color:"var(--text-muted)", marginTop:"4px" }}>
                {t("library.resources")}
              </p>
            </div>
            <ViewToggle view={view} setView={setView}/>
          </div>

          {view === "grid" ? (
            <div style={THUMB_GRID}>
              {folders.map(f => (
                <FolderCard key={f.id} folder={f} onClick={() => openFolder(f)}/>
              ))}
            </div>
          ) : (
            <div className="card" style={{ padding:0, overflow:"hidden" }}>
              {folders.map((f, i) => (
                <FolderRow key={f.id} folder={f}
                  onClick={() => openFolder(f)}
                  last={i === folders.length - 1}/>
              ))}
            </div>
          )}
        </div>
      )}
      <AudioPlayerOverlay track={playingTrack} onClose={() => setPlayingTrack(null)}/>
    </Layout>
  );
}
